<script setup lang="ts">
import type { AccordionMediaBlock } from '~/types/storyblok/accordion-media-block'
import { useImage } from '~/composables/useImage'

import { getEdgeClasses } from '~/lib/getEdgeClasses'
import { normalizeAccordionMediaBlock } from '~/types/storyblok/accordion-media-block'

interface Props {
  blok: AccordionMediaBlock
}

const props = defineProps<Props>()
const blok = toRef(props, 'blok')
const accordionMediaBlock = computed(() => normalizeAccordionMediaBlock(blok.value))

const { imageMobile, imageAlt } = useImage(
  accordionMediaBlock.value,
)
</script>

<template>
  <section
    v-editable="blok"
    :class="[
      { 'media--left': accordionMediaBlock.isMediaLeft },
      { 'media--right': accordionMediaBlock.isMediaRight },
      { 'media--top': accordionMediaBlock.isMediaTop },
      getEdgeClasses(accordionMediaBlock.edgeStyle),
    ]"
  >
    <div
      v-if="accordionMediaBlock.largeAsset"
      class="media"
    >
      <ImageResponsive
        provider="storyblok"
        :src="imageMobile"
        :alt="imageAlt"
        :large-asset="accordionMediaBlock.largeAsset"
        :ratio="accordionMediaBlock.isMediaTop ? '16:9' : 'auto'"
        mobile-ratio="4:3"
        :lazy="true"
        object-fit="cover"
      />
    </div>

    <div
      class="content"
      :class="[getEdgeClasses(accordionMediaBlock.edgeStyle)]"
    >
      <div v-if="blok">
        <div
          v-for="component in accordionMediaBlock.accordion"
          :key="component._uid"
        >
          <component
            :is="component.component"
            :key="component._uid"
            :blok="component"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import 'assets/scss/rules/edges';
@import 'assets/scss/rules/breakpoints';

.content {
    background-color: var(--white);
}

.media {
    position: relative;

    img {
        width: 100%;
    }

    &--right,
    &--left {
        display: flex;
        flex-direction: column;

        .media {
            width: 100%;

            @media (min-width: $tablet) {
                width: 50%;
                min-height: 50vw;
            }
        }

        .content {
            @media (min-width: $tablet) {
                width: 50%;
                padding: 0 1.6rem;
                height: 50vw;
                overflow: auto;
            }
        }
    }

    &--right {
        @media (min-width: $tablet) {
            flex-direction: row-reverse;
        }
    }

    &--left {
        @media (min-width: $tablet) {
            flex-direction: row;
        }
    }

    &--top {
        .media {
            width: 100%;

            img {
                width: 100%;
            }
        }
    }
}

.teeth {
    @include teeth;
    margin-top: -1.2rem;
}

.scallop {
    @include scallop;
    margin-top: -1.2rem;
}

.wave {
    @include waveFixed;
    margin-top: -1.2rem;
}
</style>
